import { useI18n } from '../contexts/I18nContext';
import { useAuth } from '../contexts/AuthContext';
import { useOrgFamilyPolicy } from './familyAlerts';
import { formatSince } from './time';

export const MAINTENANCE_TYPES = ['device_silent', 'low_battery'];
export const MOVEMENT_TYPES = ['device_silent', 'no_movement', 'no_rise'];
export const isMaintenance = (a) => MAINTENANCE_TYPES.includes(a?.event_type);

// Same rule the backend used to compose the old sentence.
export const alertPlace = (p) => p?.room || p?.device_name || null;

// Closed by the system and never seen by a person.
export const isAutoClosed = (a) => !!a?.resolved_at && !a?.resolved_by && !a?.acknowledged;

const REASON_KEYS = { 'movement resumed': 'al_reason_movement', 'sensor reporting again': 'al_reason_sensor' };
export const reasonText = (reason, t) => (reason ? (REASON_KEYS[reason] ? t(REASON_KEYS[reason]) : reason) : '');

// Minutes (number) → "3 h 4 min"; floors, never rounds up.
export function formatMinutes(min, t) {
  const n = Number(min);
  if (!Number.isFinite(n)) return '';
  if (n < 1) return t('dur_lt_min');
  const m = Math.floor(n);
  if (m < 60) return t('dur_min', { m });
  const h = Math.floor(m / 60);
  const r = m % 60;
  return r ? t('dur_h_min', { h, m: r }) : t('dur_h', { h });
}

const has = (p, k) => p && p[k] != null && p[k] !== '';

function staffText(a, t) {
  const p = a.message_params;
  const place = alertPlace(p);
  switch (a.event_type) {
    case 'fall': return t('am_fall');
    case 'sos': return t('am_sos');
    case 'low_battery': return has(p, 'device_name') ? t('am_low_battery_dev', { device: p.device_name }) : t('am_low_battery');
    case 'door_open':
    case 'window_open': {
      const base = a.event_type === 'door_open' ? 'am_door' : 'am_window';
      if (!place) return t(base);
      const detail = has(p, 'room') ? (p.label || p.device_name) : null;
      return detail ? t(`${base}_in_detail`, { place, detail }) : t(`${base}_in`, { place });
    }
    case 'device_silent':
      if (!place) break;
      return has(p, 'minutes_since_sample') ? t('am_silent', { place, dur: formatMinutes(p.minutes_since_sample, t) }) : t('am_silent_nodur', { place });
    case 'no_movement':
      if (!place) break;
      return has(p, 'minutes_since_motion') ? t('am_nomove', { place, dur: formatMinutes(p.minutes_since_motion, t) }) : t('am_nomove_nodur', { place });
    case 'no_rise':
      if (!place) break;
      return has(p, 'expected_up_by') ? t('am_norise', { place, time: p.expected_up_by }) : t('am_norise_notime', { place });
    default:
      break;
  }
  return a.message || '';
}

// Family copy: the fact and what it means; promises only what is true for this organisation.
function familyText(a, t, lang, policy) {
  const p = a.message_params;
  const place = alertPlace(p);
  const name = a.resident_name;
  const noCarers = policy?.caregiver_count === 0;
  if (a.event_type === 'device_silent' && place) {
    const sinceMs = new Date(a.created_at).getTime() - (Number(p.minutes_since_sample) || 0) * 60000;
    const time = formatSince(new Date(sinceMs).toISOString(), lang);
    const fact = name ? t('amf_silent_res', { name, time }) : t('amf_silent_room', { place, time });
    if (!policy) return fact;
    return `${fact} ${noCarers ? t('amf_contact_device') : t('amf_team')}`;
  }
  if ((a.event_type === 'no_movement' || a.event_type === 'no_rise') && place) {
    const fact = `${staffText(a, t)}. ${t('amf_sensor_ok')}`;
    if (!policy) return fact;
    return `${fact} ${noCarers ? t('amf_contact_resident', { name: name || place }) : t('amf_team')}`;
  }
  return staffText(a, t);
}

// Render an alert's sentence from event_type + message_params; falls back to the stored message.
export function useAlertText() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const isFamily = user?.role === 'family';
  const policy = useOrgFamilyPolicy(isFamily);
  return (a) => {
    if (!a) return '';
    if (!a.message_params || !a.event_type) return a.message || '';
    return isFamily ? familyText(a, t, lang, policy) : staffText(a, t);
  };
}

// Header name: the resident, else the place (room-level alerts carry a device name in resident_name), else the class.
export const alertSubject = (a, t) => ((a?.resident_uuid || a?.resident_id) ? a.resident_name : null)
  || alertPlace(a?.message_params) || a?.resident_name
  || (a?.event_type ? t(`event_${a.event_type}`) : '—');
